/**
 * Basemap registry for the MapLibre adapter.
 *
 * Every entry is a self-contained MapLibre style fragment (sources +
 * layers) so we can swap the active basemap at runtime without touching
 * the user's data layers (the data layers always sit above whatever
 * background is mounted).
 *
 * All entries here use providers that work without an API key on
 * localhost so the viewer is usable in dev / defence-air-gapped without
 * paid tile keys. For production deployments the operator can register
 * additional entries (Mapbox, MapTiler, ArcGIS Premium, etc) by editing
 * this file or supplying a runtime override.
 */

import type { Map as MLMap, RasterSourceSpecification, LayerSpecification } from 'maplibre-gl';
import type { IconName } from '@/design';

export type BasemapId =
  | 'none'
  | 'osm'
  | 'esri-imagery'
  | 'carto-positron'
  | 'carto-dark-matter'
  | 'opentopomap';

export interface BasemapDefinition {
  id: BasemapId;
  label: string;
  description: string;
  /**
   * Tokenized thumbnail glyph for the basemap picker (no third-party tile
   * imagery in chrome per `ICON_SYSTEM_PLAYBOOK.md` "Brand product icons":
   * monochrome Material Symbols only). Resolved through the `<Icon>`
   * primitive so the color follows the surface ink token.
   */
  thumbnailIcon: IconName;
  /** Mapbox-style source specs keyed by source id. */
  sources: Record<string, RasterSourceSpecification>;
  /** Layers to inject above the canvas background. */
  layers: LayerSpecification[];
}

const BASEMAP_PREFIX = 'basemap';

function buildXyzBasemap(
  id: BasemapId,
  label: string,
  description: string,
  tileUrls: string[],
  attribution: string,
  thumbnailIcon: IconName,
  maxzoom = 19,
): BasemapDefinition {
  const sourceId = `${BASEMAP_PREFIX}-${id}-src`;
  const layerId = `${BASEMAP_PREFIX}-${id}-layer`;
  return {
    id,
    label,
    description,
    thumbnailIcon,
    sources: {
      [sourceId]: {
        type: 'raster',
        tiles: tileUrls,
        tileSize: 256,
        attribution,
        maxzoom,
      },
    },
    layers: [
      {
        id: layerId,
        type: 'raster',
        source: sourceId,
        paint: { 'raster-opacity': 1 },
      },
    ],
  };
}

export const BASEMAPS: BasemapDefinition[] = [
  {
    id: 'none',
    label: 'No basemap',
    description: 'Transparent background; only your data layers are rendered.',
    thumbnailIcon: 'visibility_off',
    sources: {},
    layers: [],
  },
  buildXyzBasemap(
    'osm',
    'OpenStreetMap',
    'OpenStreetMap standard street map.',
    [
      'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
      'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
      'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
    ],
    '© OpenStreetMap contributors',
    'map',
    19,
  ),
  buildXyzBasemap(
    'esri-imagery',
    'Esri World Imagery (satellite)',
    'High-resolution satellite imagery from Esri.',
    [
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    ],
    'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
    'public',
    19,
  ),
  buildXyzBasemap(
    'carto-positron',
    'CartoDB Positron (light)',
    'Minimal light cartographic style ideal for raster overlays.',
    [
      'https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
      'https://b.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
      'https://c.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
    ],
    '© OpenStreetMap contributors © CARTO',
    'wb_sunny',
    19,
  ),
  buildXyzBasemap(
    'carto-dark-matter',
    'CartoDB Dark Matter',
    'Dark cartographic style ideal for night and high-contrast overlays.',
    [
      'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
      'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
      'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
    ],
    '© OpenStreetMap contributors © CARTO',
    'wb_shade',
    19,
  ),
  buildXyzBasemap(
    'opentopomap',
    'OpenTopoMap',
    'Topographic style with contours and shaded relief.',
    ['https://a.tile.opentopomap.org/{z}/{x}/{y}.png'],
    '© OpenStreetMap contributors, SRTM, OpenTopoMap (CC-BY-SA)',
    'landscape',
    17,
  ),
];

const BASEMAP_BY_ID = new Map(BASEMAPS.map((b) => [b.id, b]));

export const DEFAULT_BASEMAP_ID: BasemapId = 'esri-imagery';
const STORAGE_KEY = 'geoviewer.basemap';

export function getBasemap(id: BasemapId): BasemapDefinition {
  return BASEMAP_BY_ID.get(id) ?? BASEMAP_BY_ID.get(DEFAULT_BASEMAP_ID)!;
}

export function loadPreferredBasemapId(): BasemapId {
  if (typeof window === 'undefined') return DEFAULT_BASEMAP_ID;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && BASEMAP_BY_ID.has(v as BasemapId)) return v as BasemapId;
  } catch {
    /* ignore storage failures (private mode, etc) */
  }
  return DEFAULT_BASEMAP_ID;
}

export function savePreferredBasemapId(id: BasemapId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

/**
 * Remove every basemap source / layer registered through this module from
 * the given map. Safe to call repeatedly. Used before swapping basemaps.
 */
function removeAllBasemaps(map: MLMap): void {
  const style = map.getStyle();
  if (!style) return;
  for (const layer of style.layers ?? []) {
    if (layer.id.startsWith(BASEMAP_PREFIX)) {
      try {
        map.removeLayer(layer.id);
      } catch {
        /* ignore */
      }
    }
  }
  for (const sourceId of Object.keys(style.sources ?? {})) {
    if (sourceId.startsWith(BASEMAP_PREFIX)) {
      try {
        map.removeSource(sourceId);
      } catch {
        /* ignore */
      }
    }
  }
}

/**
 * Apply a basemap to a mounted MapLibre map. Inserts new sources/layers
 * directly above the `background` style layer so user data layers stay
 * on top.
 */
export function applyBasemap(map: MLMap, id: BasemapId): void {
  const def = getBasemap(id);
  removeAllBasemaps(map);
  for (const [sourceId, spec] of Object.entries(def.sources)) {
    map.addSource(sourceId, spec);
  }
  // Find the first non-basemap, non-background layer to insert before so
  // basemaps stay below user data; null = insert at the top of the stack.
  const style = map.getStyle();
  const beforeId = (style.layers ?? []).find(
    (l) => l.id !== 'background' && !l.id.startsWith(BASEMAP_PREFIX),
  )?.id;
  for (const layer of def.layers) {
    map.addLayer(layer, beforeId);
  }
}
